import type { ForqSupabaseClient } from './client';
import {
  normalizeBugReports,
  normalizeOwnBugReports,
  type BugReportContext,
  type BugReportGateway,
  type BugReportInput,
  type BugReportRecord,
  type BugStatus,
  type OwnBugReport,
} from '@forq/platform-data';
import { SupabaseOperationError } from './errors';

function messageOf(error: { readonly message: string } | null): string {
  return error?.message ?? 'Unknown Supabase error.';
}

/**
 * Fehlermeldungen gegen die Datenbank.
 *
 * Vier RPCs, drei verschiedene Zugangsstufen — und die Trennung liegt bewusst
 * in der Datenbank, nicht hier: `report_bug` und `list_my_bug_reports` prüfen
 * nur die Mitgliedschaft im Mandanten, die beiden `admin_*` prüfen ein Recht.
 * Dieser Adapter reicht durch und formt um, mehr nicht.
 */
export class SupabaseBugReportGateway implements BugReportGateway {
  constructor(private readonly client: ForqSupabaseClient) {}

  async report(tenantId: string, input: BugReportInput, context: BugReportContext): Promise<void> {
    const { error } = await this.client.rpc('report_bug', {
      p_tenant_id: tenantId,
      p_title: input.title.trim(),
      p_description: input.description.trim(),
      p_severity: input.severity,
      p_route: context.route,
      p_app_version: context.appVersion,
      p_user_agent: context.userAgent,
      p_viewport: context.viewport,
    });
    if (error) throw new SupabaseOperationError('bugs.report', messageOf(error));
  }

  async listMine(tenantId: string): Promise<OwnBugReport[]> {
    const { data, error } = await this.client.rpc('list_my_bug_reports', { p_tenant_id: tenantId });
    if (error) throw new SupabaseOperationError('bugs.listMine', messageOf(error));
    return normalizeOwnBugReports(data ?? []);
  }

  async listAll(tenantId: string): Promise<BugReportRecord[]> {
    const { data, error } = await this.client.rpc('admin_list_bug_reports', { p_tenant_id: tenantId });
    if (error) throw new SupabaseOperationError('bugs.listAll', messageOf(error));
    return normalizeBugReports(data ?? []);
  }

  async setStatus(
    tenantId: string,
    reportId: string,
    status: BugStatus,
    note: string,
  ): Promise<BugReportRecord[]> {
    const { error } = await this.client.rpc('admin_set_bug_status', {
      p_tenant_id: tenantId,
      p_report_id: reportId,
      p_status: status,
      p_note: note.trim(),
    });
    if (error) throw new SupabaseOperationError('bugs.setStatus', messageOf(error));
    // Frisch nachladen statt lokal zu flicken: der Stand kommt aus der
    // Datenbank, und zwei Bearbeiter am selben Eintrag sind bei einer
    // Alphaphase keine Seltenheit.
    return this.listAll(tenantId);
  }
}
